// @ts-nocheck
/**
 * FilesContainer - Custom element that displays product files in an accordion/drawer format
 * Data structure: window.AkeneoFilesIndex array with products containing files
 */
// Note: Redundant local helpers removed — use window.FileUtils from files-card-helpers.js

// Minimal fallback: files-card-helpers.js is currently only loaded when predictive search is enabled.
// This keeps the IES rename behavior working on the files-search page without requiring Liquid changes.
(function () {
  try {
    window.FilesSearchAPI = window.FilesSearchAPI || {};
    if (typeof window.FilesSearchAPI.downloadIesAsIes === 'function') return;

    const toIesTxtUrl = urlStr => {
      try {
        const parsed = new URL(urlStr, window.location.href);
        const lowerPath = String(parsed.pathname || '').toLowerCase();
        if (lowerPath.endsWith('.ies.txt')) return parsed.href;
        if (lowerPath.endsWith('.ies')) {
          parsed.pathname = parsed.pathname + '.txt';
          return parsed.href;
        }
        return parsed.href;
      } catch (e) {
        return String(urlStr || '');
      }
    };

    const normalizeIesDownloadName = name => {
      const str = String(name || '');
      if (str.toLowerCase().endsWith('.ies.txt')) return str.slice(0, -4);
      if (str.toLowerCase().endsWith('.ies')) return str;
      return str.replace(/\.+$/, '') + '.ies';
    };

    const triggerNativeDownload = (href, filename) => {
      const a = document.createElement('a');
      a.href = href;
      if (filename) a.setAttribute('download', filename);
      a.rel = 'noopener noreferrer';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    window.FilesSearchAPI.downloadIesAsIes = async function (urlStr, opts) {
      const options = opts || {};
      const timeoutMs = Number.isFinite(options.timeoutMs) ? options.timeoutMs : 20000;
      const maxBlobBytes = Number.isFinite(options.maxBlobBytes)
        ? options.maxBlobBytes
        : 10 * 1024 * 1024;

      const requestUrl = toIesTxtUrl(urlStr);
      const downloadName = normalizeIesDownloadName(options.suggestedName || 'download.ies');
      if (!requestUrl) return;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const signal = options.signal || controller.signal;

      try {
        const response = await fetch(requestUrl, { mode: 'cors', signal });
        if (!response.ok) throw new Error(`http_${response.status}`);

        const totalBytes = parseInt(response.headers.get('content-length') || '0', 10) || null;
        if (totalBytes && totalBytes > maxBlobBytes) throw new Error('too_large');

        const blob = await response.blob();
        if (blob.size > maxBlobBytes) throw new Error('too_large');

        const blobUrl = URL.createObjectURL(blob);
        triggerNativeDownload(blobUrl, downloadName);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
      } catch (err) {
        try {
          triggerNativeDownload(requestUrl, downloadName);
        } catch (e2) {
          window.location.href = requestUrl;
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };
  } catch (e) {}
})();

class FilesContainer extends HTMLElement {
  constructor() {
    super();
    this._data = [];
    // Store a single bound click handler to avoid duplicate listeners
    this._onDownloadClick = this.handleDownloadClick.bind(this);
  }

  set data(value) {
    this._data = value;
    this.render();
  }

  get data() {
    return this._data;
  }

  connectedCallback() {
    // Initial render when element is added to the DOM
    this.render();
    // Use stored bound handler to avoid duplicates when re-attaching
    this.addEventListener('click', this._onDownloadClick);
  }

  disconnectedCallback() {
    // Cleanup the stored handler when element is detached
    this.removeEventListener('click', this._onDownloadClick);
  }

  render() {
    // Show nothing initially or when no search results
    if (!this._data || this._data.length === 0) {
      this.innerHTML = '';
      this.style.display = 'none';
      return;
    }

    this.style.display = 'block';

    const translations = window.filesSearchTranslations || {};
    const isPlaceholder = this.dataset.mode === 'placeholder';
    const searchResultsLabel = translations.search_results || 'Search Results';
    const placeholderLabel = translations.browse_files || translations.files || 'Files';
    const productsLabel = translations.products || 'Products';
    const headerLabel = isPlaceholder ? productsLabel : searchResultsLabel;

    this.innerHTML = `
      <div class="files-results-drawer">
        <div class="files-results-header">
          <h3>${headerLabel} (${this._data.length})</h3>
        </div>
        <div class="files-results-list">
          ${this._data.map(product => this.renderProductCard(product)).join('')}
        </div>
      </div>
    `;
    this.hydrateInlineDocIcons();
  }

  hydrateInlineDocIcons() {
    const images = this.querySelectorAll('.files-results-drawer img.doc-icon-img');
    if (!images || images.length === 0) return;

    const cache =
      window.__filesSearchInlineSvgCache || (window.__filesSearchInlineSvgCache = new Map());

    const parseFirstSvg = svgText => {
      const template = document.createElement('template');
      template.innerHTML = String(svgText || '').trim();
      return template.content.querySelector('svg');
    };

    const toAbsoluteUrl = urlStr => {
      try {
        return new URL(urlStr, window.location.href).href;
      } catch (e) {
        return null;
      }
    };

    const applySvg = (imgEl, svgText) => {
      if (!imgEl || !svgText) return;
      const svg = parseFirstSvg(svgText);
      if (!svg) return;
      svg.classList.add('doc-icon');
      svg.setAttribute('aria-hidden', 'true');
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      imgEl.replaceWith(svg);
    };

    for (const imgEl of images) {
      if (!imgEl || imgEl.dataset.inlineSvgApplied === '1') continue;
      imgEl.dataset.inlineSvgApplied = '1';
      const src = imgEl.getAttribute('src');
      const absUrl = toAbsoluteUrl(src);
      if (!absUrl) continue;

      const cached = cache.get(absUrl);
      if (cached && cached.status === 'ready' && cached.svgText) {
        applySvg(imgEl, cached.svgText);
        continue;
      }

      if (cached && cached.status === 'pending' && Array.isArray(cached.waiters)) {
        cached.waiters.push(svgText => applySvg(imgEl, svgText));
        continue;
      }

      const entry = {
        status: 'pending',
        svgText: null,
        waiters: [svgText => applySvg(imgEl, svgText)],
      };
      cache.set(absUrl, entry);

      fetch(absUrl, { credentials: 'same-origin' })
        .then(response => {
          if (!response || !response.ok) throw new Error('svg_fetch_failed');
          return response.text();
        })
        .then(text => {
          if (!/\<svg[\s\>]/i.test(text)) throw new Error('not_svg');
          entry.status = 'ready';
          entry.svgText = text;
          const waiters = entry.waiters || [];
          entry.waiters = [];
          for (const fn of waiters) {
            try {
              fn(text);
            } catch (e) {}
          }
        })
        .catch(() => {
          cache.delete(absUrl);
        });
    }
  }

  renderProductCard(product) {
    // Flatten all valid files across types into a single list
    const flatFiles = [];
    if (product.files && typeof product.files === 'object') {
      for (const [typeKey, files] of Object.entries(product.files)) {
        const validFiles = this.normalizeFileEntries(files);
        if (validFiles.length === 0) continue;
        const formattedType = typeKey
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        for (const f of validFiles) {
          flatFiles.push({ file: f, typeKey, formattedType });
        }
      }
    }

    const totalFiles = flatFiles.length;

    // Get translations
    const translations = window.filesSearchTranslations || {};
    const fileLabel =
      totalFiles !== 1 ? translations.files || 'files' : translations.file || 'file';
    const skuLabel = translations.sku || 'SKU';
    const noFilesLabel = translations.no_files || 'No files available';

    const productTitle = product.title || translations.untitled_product || 'Untitled Product';
    const productUrlBase =
      product && product.url
        ? product.url
        : product && product.handle
          ? `/products/${product.handle}`
          : null;
    const productUrl = this.appendVariantParam(productUrlBase, product && product.variant_id);
    const rawThumbUrl =
      product && product.productImage && product.productImage.url
        ? product.productImage.url
        : product && product.image_url
          ? product.image_url
          : '';
    const thumbUrl = this.appendImageWidth(rawThumbUrl, 200);
    const thumbAlt =
      product && product.productImage && product.productImage.alt
        ? product.productImage.alt
        : productTitle || 'Product image';

    return `
      <div class="product-file-card">
        <div class="product-card-header">
          ${
            thumbUrl
              ? `
            <a class="product-thumb" href="${productUrl || '#'}" aria-label="${this.esc(productTitle)}">
              <img src="${thumbUrl}" alt="${this.esc(thumbAlt)}" width="64" height="64" loading="lazy" />
            </a>
          `
              : ``
          }
          <div class="product-title-section">
            ${productUrl ? `<a href="${productUrl}" class="product-link">` : ``}
              <div class="product-title">${this.esc(productTitle)}</div>
            ${productUrl ? `</a>` : ``}
            <div class="product-sku">${this.esc(skuLabel)}: ${this.esc(product.sku || 'N/A')}</div>
          </div>
          <div class="file-count-badge">${totalFiles} ${fileLabel}</div>
        </div>

        ${
          totalFiles > 0
            ? `
          <div class="files-icons-row">
            ${flatFiles.map((entry, index) => this.renderFileItem(entry.file, index, entry.typeKey, entry.formattedType, productTitle)).join('')}
          </div>
        `
            : `<div class="no-files">${noFilesLabel}</div>`
        }
      </div>
    `;
  }

  // Safely append a width parameter to Shopify CDN image URLs
  // If a width is already present, leave it as-is.
  appendImageWidth(url, width = 200) {
    try {
      if (!url) return '';
      const str = String(url);
      // If width is already specified, do not append again
      if (/[?&]width=\d+/i.test(str)) return str;
      const separator = str.includes('?') ? '&' : '?';
      return `${str}${separator}width=${encodeURIComponent(width)}`;
    } catch (e) {
      return url || '';
    }
  }

  // Append variant param without dropping existing query params.
  appendVariantParam(url, variantId) {
    if (!url) return url;
    const raw = String(variantId || '').trim();
    const id = raw.includes('/') ? raw.split('/').pop() : raw;
    if (!id) return url;
    const hasQuery = url.includes('?');
    const joiner = hasQuery ? '&' : '?';
    return `${url}${joiner}variant=${encodeURIComponent(id)}`;
  }

  renderFileType(fileType, files, productTitle) {
    if (!files || files.length === 0) return '';

    // Filter out files without valid URLs (supports string URLs)
    const validFiles = this.normalizeFileEntries(files);

    if (validFiles.length === 0) return '';

    // Format file type name (e.g., "spec_sheet" -> "Spec Sheet")
    const formattedType = fileType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return `
      <div class="file-type-group">
        <div class="file-type-label">${formattedType}</div>
        ${validFiles.map((file, index) => this.renderFileItem(file, index, fileType, formattedType, productTitle)).join('')}
      </div>
    `;
  }

  renderFileItem(file, index, fileTypeKey, fileTypeName, productTitle) {
    const openInBrowser = this.getAttribute('data-open-in-browser') === 'true';
    const fileExtension =
      window.FileUtils && typeof window.FileUtils.getFileExtension === 'function'
        ? window.FileUtils.getFileExtension(file.url)
        : 'file';
    const displayName = `${fileTypeName}`;
    const downloadName =
      window.FileUtils && typeof window.FileUtils.getFileNameFromUrl === 'function'
        ? window.FileUtils.getFileNameFromUrl(file.url)
        : displayName;
    const typeKeyLower = (fileTypeKey || '').toLowerCase();
    const extUpper = (fileExtension || '').toUpperCase();
    const isRevit = typeKeyLower.includes('revit');
    const isIES = typeKeyLower.includes('ies');
    const forceDownload = isIES === true;
    const revitDisclaimer =
      this.getAttribute('data-revit-disclaimer') ||
      'Users must have an Autodesk account to download and view Revit files.';

    // Choose icon style based on file type group (manual, ies, spec, revit). Fallback to extension label if unknown.
    let iconSvg = '';
    if (typeKeyLower.includes('manual')) {
      iconSvg =
        window.FileUtils && typeof window.FileUtils.getDocIconSvg === 'function'
          ? window.FileUtils.getDocIconSvg('pdf')
          : '';
    } else if (typeKeyLower.includes('ies')) {
      iconSvg =
        window.FileUtils && typeof window.FileUtils.getDocIconSvg === 'function'
          ? window.FileUtils.getDocIconSvg('ies')
          : '';
    } else if (typeKeyLower.includes('spec')) {
      iconSvg =
        window.FileUtils && typeof window.FileUtils.getDocIconSvg === 'function'
          ? window.FileUtils.getDocIconSvg('spec')
          : '';
    } else if (typeKeyLower.includes('revit')) {
      iconSvg =
        window.FileUtils && typeof window.FileUtils.getDocIconSvg === 'function'
          ? window.FileUtils.getDocIconSvg('revit')
          : '';
    } else {
      iconSvg =
        window.FileUtils && typeof window.FileUtils.getDocIconSvg === 'function'
          ? window.FileUtils.getDocIconSvg('ext', extUpper)
          : '';
    }

    // Build icon-only link with instant tooltip; no filename text.
    const tooltipText = `${this.esc(displayName)}${extUpper ? ` (${this.esc(extUpper)})` : ''}`;
    const actionLabel = openInBrowser && !forceDownload ? 'Open' : 'Download';
    const linkBehaviorAttrs = forceDownload
      ? `download="${this.esc(downloadName)}" data-force-download="true"`
      : openInBrowser
        ? `target="_blank" rel="noopener noreferrer" data-open-in-browser="true"`
        : `download="${this.esc(downloadName)}"`;
    return `
      <div class="file-item">
        <a href="${file.url}"
           ${linkBehaviorAttrs}
           class="file-download-link file-icon-link"
           aria-label="${actionLabel} ${this.esc(displayName)}">
          ${iconSvg}
          <span class="file-icon-tooltip">${tooltipText}</span>
          ${isRevit ? `<span class="file-icon-disclaimer">${this.esc(revitDisclaimer)}</span>` : ``}
          <span class="visually-hidden">${this.esc(fileTypeName)} file</span>
        </a>
      </div>
    `;
  }

  normalizeFileEntries(files) {
    if (!Array.isArray(files) || files.length === 0) return [];
    return files
      .map(file => {
        if (typeof file === 'string') return { url: file };
        if (file && typeof file === 'object') return file;
        return null;
      })
      .filter(file => file && file.url && String(file.url).trim() !== '');
  }
  // Lightweight wrappers that delegate to shared window.FileUtils with safe fallbacks
  esc(str) {
    if (window.FileUtils && typeof window.FileUtils.escapeHtml === 'function') {
      try {
        return window.FileUtils.escapeHtml(str);
      } catch (e) {}
    }
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  fileNameFromUrl(url) {
    if (window.FileUtils && typeof window.FileUtils.getFileNameFromUrl === 'function') {
      try {
        return window.FileUtils.getFileNameFromUrl(url);
      } catch (e) {}
    }
    return 'download';
  }

  fileExt(url) {
    if (window.FileUtils && typeof window.FileUtils.getFileExtension === 'function') {
      try {
        return window.FileUtils.getFileExtension(url);
      } catch (e) {}
    }
    return 'file';
  }

  docIcon(kind, extLabel) {
    if (window.FileUtils && typeof window.FileUtils.getDocIconSvg === 'function') {
      try {
        return window.FileUtils.getDocIconSvg(kind, extLabel);
      } catch (e) {}
    }
    return '';
  }

  getFileIcon(extension) {
    const icons = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      svg: '🎨',
      zip: '🗜️',
      rar: '🗜️',
      ies: '💡',
    };
    return icons[extension] || '📎';
  }

  async handleDownloadClick(event) {
    if (window.FilesSearchAPI && typeof window.FilesSearchAPI.handleFileLinkClick === 'function') {
      window.FilesSearchAPI.handleFileLinkClick(event, {
        openInBrowser: this.getAttribute('data-open-in-browser') === 'true',
      });
      return;
    }

    const anchor = event.target.closest('.file-download-link');
    if (!anchor) return;
    const rawHref = anchor.getAttribute('href') || '';
    const isIes = String(rawHref || '')
      .toLowerCase()
      .includes('.ies');
    const forceDownload = isIes;
    const openInBrowser =
      anchor.getAttribute('data-open-in-browser') === 'true' ||
      this.getAttribute('data-open-in-browser') === 'true';
    if (openInBrowser && !forceDownload) return;
    event.preventDefault();

    const downloadAttr = anchor.getAttribute('download') || '';

    try {
      if (
        isIes &&
        window.FilesSearchAPI &&
        typeof window.FilesSearchAPI.downloadIesAsIes === 'function'
      ) {
        window.FilesSearchAPI.downloadIesAsIes(rawHref, { suggestedName: downloadAttr });
        return;
      }
      const tmpLink = document.createElement('a');
      tmpLink.href = rawHref;
      if (downloadAttr) tmpLink.download = downloadAttr;
      tmpLink.rel = 'noopener noreferrer';
      tmpLink.style.display = 'none';
      document.body.appendChild(tmpLink);
      tmpLink.click();
      tmpLink.remove();
    } catch (e2) {
      window.location.href = rawHref;
    }
  }
}

customElements.define('files-container', FilesContainer);

/**
 * FilesSearch - Custom search input element with fuzzy matching and auto-suggestions
 * Searches through window.AkeneoFilesIndex for products and files
 */
class FilesSearch extends HTMLElement {
  constructor() {
    super();
    this.dataReady = false;
    // Track UI/search state
    this.hasSearched = false; // becomes true after a user submits a search
    this.activity = 'idle';
    this.placeholderLimit = 0;
    this.placeholderDismissed = false;
    this.iconRetryCount = 0;
  }

  async connectedCallback() {
    // Move initialization to connectedCallback to ensure DOM is ready
    this.filesContainer = document.querySelector('files-container');

    const placeholderLimitAttr = parseInt(this.getAttribute('data-placeholder-limit'), 10);
    if (Number.isFinite(placeholderLimitAttr) && placeholderLimitAttr >= 0) {
      this.placeholderLimit = Math.min(placeholderLimitAttr, 24);
    }

    if (!this.filesContainer) {
      console.error('files-container element not found');
      this.showError('Search container not found. Please refresh the page.');
      return;
    }

    // Wait for localized overlay merge before reading AkeneoFilesIndex.
    await this.waitForAkeneoIndex();
    // Get all products from the global index
    this.allProducts = window.AkeneoFilesIndex || [];

    if (this.allProducts.length === 0) {
      console.warn('window.AkeneoFilesIndex is empty or not defined');
      this.showError('Product data is not available. Please try again later.');
      this.dataReady = false;
    } else {
      this.dataReady = true;
    }

    this.renderInput();
    this.renderSearchButton();
    this.renderClearButton();
    this.renderSuggestionsBox();
    this.renderLoadingSpinner();
    this.bindEvents();
    this.showPlaceholder();
    this.waitForIcons();
  }

  // Resolve when AkeneoFilesIndex is ready or after a short timeout.
  waitForAkeneoIndex(timeoutMs = 6000) {
    if (window.AkeneoFilesIndexReady === true) return Promise.resolve();
    if (Array.isArray(window.AkeneoFilesIndex) && window.AkeneoFilesIndex.length > 0) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        document.removeEventListener('akeneo-files-index:ready', onReady);
        resolve();
      };
      const onReady = () => finish();
      document.addEventListener('akeneo-files-index:ready', onReady, { once: true });
      setTimeout(() => finish(), timeoutMs);
    });
  }

  showError(message) {
    if (this.filesContainer) {
      const translations = window.filesSearchTranslations || {};
      const errorLabel = translations.error || 'Error';

      this.filesContainer.dataset.mode = 'results';
      this.filesContainer.innerHTML = `
        <div class="files-error-message" style="padding: 1rem; color: #d32f2f; background: #ffebee; border-radius: 4px; margin-top: 1rem;">
          <strong>⚠️ ${errorLabel}:</strong> ${message}
        </div>
      `;
      this.filesContainer.style.display = 'block';
    }
  }

  productHasValidFiles(product) {
    if (!product || !product.files || typeof product.files !== 'object') return false;
    for (const files of Object.values(product.files)) {
      if (!Array.isArray(files) || files.length === 0) continue;
      if (files.some(file => file && file.url && String(file.url).trim() !== '')) {
        return true;
      }
    }
    return false;
  }

  getPlaceholderProducts(limit = this.placeholderLimit) {
    const products = this.allProducts || [];
    const results = [];
    for (const product of products) {
      if (!this.productHasValidFiles(product)) continue;
      results.push(product);
      if (results.length >= limit) break;
    }
    return results;
  }

  showPlaceholder() {
    if (!this.filesContainer || !this.dataReady) return;
    if (this.hasSearched || this.activity === 'searching' || this.placeholderDismissed) return;
    const placeholderProducts = this.getPlaceholderProducts(this.placeholderLimit);
    if (placeholderProducts.length === 0) return;
    this.filesContainer.dataset.mode = 'placeholder';
    this.filesContainer.data = placeholderProducts;
    this.waitForIcons();
  }

  waitForIcons() {
    if (this.iconRetryCount >= 20) return;
    if (
      window.FileUtils &&
      typeof window.FileUtils.getDocIconSvg === 'function' &&
      window.fileIcons
    ) {
      this.refreshPlaceholderIcons();
      return;
    }
    this.iconRetryCount += 1;
    setTimeout(() => this.waitForIcons(), 100);
  }

  refreshPlaceholderIcons() {
    if (!this.filesContainer || this.filesContainer.dataset.mode !== 'placeholder') return;
    const currentData = this.filesContainer.data || [];
    if (!currentData.length) return;
    this.filesContainer.data = currentData;
  }

  clearPlaceholder() {
    if (!this.filesContainer) return;
    if (this.filesContainer.dataset.mode !== 'placeholder') return;
    this.filesContainer.dataset.mode = '';
    this.filesContainer.data = [];
  }

  bindEvents() {
    // Debounced suggestions handler so we can cancel/ignore while searching
    this.debouncedShowSuggestions = this.debounce(this.showSuggestions.bind(this), 200);
    // Show suggestions as user types and keep Search available before first submit
    this.input.addEventListener('input', () => {
      const value = this.input.value.trim();
      // Typing resets the searched state so the Search button remains available
      this.hasSearched = false;
      this.activity = 'idle';
      if (value.length === 0) {
        this.placeholderDismissed = false;
        this.showPlaceholder();
      } else if (value.length >= 3) {
        this.placeholderDismissed = true;
        this.clearPlaceholder();
      }
      this.toggleClearButton();
      this.debouncedShowSuggestions();
    });

    this.clearBtn.addEventListener('click', () => this.clearInput());
    // Allow click/tap on the search icon/button
    this.searchBtn.addEventListener('click', () => this.performSearch());

    // Trigger full search on Enter key (keydown is more reliable across browsers)
    this.input.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.performSearch();
      }
    });

    // Hide suggestions when the input loses focus
    this.input.addEventListener('blur', () => {
      this.hideSuggestions();
    });

    // Close suggestions when clicking outside
    document.addEventListener('click', e => {
      if (!this.contains(e.target)) {
        this.hideSuggestions();
      }
    });
  }

  renderInput() {
    this.input = document.createElement('input');
    const placeholder = this.getAttribute('placeholder') || 'Search...';
    // Use search input type to surface native mobile UX improvements
    this.input.setAttribute('type', 'search');
    this.input.setAttribute('placeholder', placeholder);
    this.input.setAttribute('autocomplete', 'off');
    // Provide a hint to mobile keyboards to show a Search action
    this.input.setAttribute('enterkeyhint', 'search');
    this.input.classList.add('files-search-input');
    this.appendChild(this.input);
  }

  renderClearButton() {
    this.clearBtn = document.createElement('button');
    this.clearBtn.innerHTML = '×';
    this.clearBtn.classList.add('clear-btn');
    this.clearBtn.style.display = 'none';
    this.clearBtn.setAttribute('type', 'button');
    this.clearBtn.setAttribute('aria-label', 'Clear search');
    this.appendChild(this.clearBtn);
  }

  renderSearchButton() {
    this.searchBtn = document.createElement('button');
    this.searchBtn.innerHTML = `<svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.89833 8.95956C3.15515 9.56386 3.52848 10.1117 3.99701 10.5717C4.46554 11.0317 5.02009 11.3949 5.62899 11.6406C6.2379 11.8863 6.88923 12.0097 7.54582 12.0037C8.2024 11.9977 8.85137 11.8624 9.45567 11.6056C10.06 11.3488 10.6078 10.9754 11.0678 10.5069C11.5278 10.0384 11.891 9.48382 12.1367 8.87492C12.3824 8.26601 12.5058 7.61468 12.4998 6.95809C12.4938 6.30151 12.3585 5.65255 12.1017 5.04825C11.8448 4.44395 11.4715 3.89615 11.003 3.43613C10.5345 2.97611 9.97991 2.61288 9.37101 2.36717C8.7621 2.12147 8.11077 1.9981 7.45418 2.00412C6.7976 2.01013 6.14863 2.14541 5.54433 2.40223C4.94003 2.65905 4.39223 3.03239 3.93221 3.50091C3.4722 3.96944 3.10896 4.52399 2.86326 5.13289C2.61756 5.7418 2.49419 6.39314 2.50021 7.04972C2.50623 7.7063 2.64151 8.35527 2.89833 8.95956Z" stroke="#253643" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.5 11.0039L14.5 14.0039" stroke="#253643" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
    this.searchBtn.classList.add('search-btn');
    this.searchBtn.setAttribute('type', 'button');
    this.searchBtn.setAttribute('aria-label', 'Search');
    this.appendChild(this.searchBtn);
  }

  renderSuggestionsBox() {
    this.suggestionsBox = document.createElement('ul');
    this.suggestionsBox.classList.add('files-suggestions-box');
    this.suggestionsBox.style.display = 'none';
    this.appendChild(this.suggestionsBox);
  }

  renderLoadingSpinner() {
    const translations = window.filesSearchTranslations || {};
    const searchingLabel = translations.searching || 'Searching...';

    this.loadingSpinner = document.createElement('div');
    this.loadingSpinner.classList.add('files-loading-spinner');
    this.loadingSpinner.innerHTML = `
      <div class="spinner-icon">⏳</div>
      <span>${searchingLabel}</span>
    `;
    this.loadingSpinner.style.display = 'none';
    // Append to parent container, not the search input
    const wrapper = this.closest('.files-search-wrapper');
    if (wrapper) {
      wrapper.appendChild(this.loadingSpinner);
    }
  }

  toggleClearButton() {
    const hasText = this.input.value.length > 0;

    if (!this.hasSearched) {
      // Before first search: always show searchBtn, never show clearBtn
      this.searchBtn.style.display = 'block';
      this.clearBtn.style.display = 'none';
    } else {
      // After search: show clearBtn only if hasText, never show searchBtn
      this.searchBtn.style.display = 'none';
      this.clearBtn.style.display = hasText ? 'block' : 'none';
    }
  }

  clearInput() {
    this.input.value = '';
    this.hideSuggestions();
    this.filesContainer.data = [];
    this.hasSearched = false;
    this.activity = 'idle';
    this.placeholderDismissed = false;
    this.showPlaceholder();
    this.toggleClearButton();
    this.input.focus();
  }

  showSuggestions() {
    const query = this.input.value.toLowerCase().trim();
    // If we're in the middle of a submitted search, don't reopen suggestions
    if (this.activity === 'searching') return;

    this.toggleClearButton();

    // If query is empty or too short, hide suggestions and clear results
    if (query.length === 0) {
      this.hideSuggestions();
      this.placeholderDismissed = false;
      this.showPlaceholder();
      this.hideLoading();
      return;
    }

    if (!this.dataReady) {
      const translations = window.filesSearchTranslations || {};
      this.showError(translations.not_ready || 'Product data is still loading. Please wait...');
      return;
    }

    // Show loading indicator while typing
    this.showLoading();

    // Check for exact match first (prioritize exact SKU/title matches)
    const exactMatch = this.allProducts.find(product => {
      return (
        product.sku?.toLowerCase() === query ||
        product.title?.toLowerCase() === query ||
        product.handle?.toLowerCase() === query
      );
    });

    let suggestions = [];

    if (exactMatch) {
      // Show exact match first
      suggestions = [exactMatch];
    } else {
      // Get fuzzy matches, limit to top 5
      suggestions = this.searchProducts(query).slice(0, 5);
    }

    this.hideLoading();

    if (suggestions.length > 0) {
      this.renderSuggestions(suggestions);
    } else {
      // Show "no suggestions" message
      this.showNoSuggestions(query);
    }
  }

  renderSuggestions(suggestions) {
    this.suggestionsBox.innerHTML = '';

    suggestions.forEach(product => {
      const li = document.createElement('li');
      li.classList.add('suggestion-item');
      li.innerHTML = `
        <div class="suggestion-content">
          <span class="suggestion-title">${product.title || 'Untitled'}</span>
          <span class="suggestion-sku">${product.sku || 'No SKU'}</span>
        </div>
      `;

      const onSelect = e => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        this.selectSuggestion(product);
      };

      if (window.PointerEvent) {
        li.addEventListener('pointerdown', onSelect);
      } else {
        li.addEventListener('mousedown', onSelect);
        li.addEventListener('touchstart', onSelect, { passive: false });
      }

      this.suggestionsBox.appendChild(li);
    });

    this.suggestionsBox.style.display = 'block';
  }

  hideSuggestions() {
    if (this.suggestionsBox) {
      this.suggestionsBox.style.display = 'none';
      this.suggestionsBox.innerHTML = '';
    }
  }

  showNoSuggestions(query) {
    const translations = window.filesSearchTranslations || {};
    const noResultsLabel = translations.no_results_for || 'No results found for';

    this.suggestionsBox.innerHTML = `
      <li class="no-suggestions-message">
        <div style="padding: 1rem; text-align: center; color: #666;">
          <p style="margin: 0; font-size: 0.9rem;">${noResultsLabel} "${query}"</p>
        </div>
      </li>
    `;
    this.suggestionsBox.style.display = 'block';
  }

  selectSuggestion(product) {
    // Fill input with selected SKU
    this.input.value = product.sku || product.title || '';
    this.hideSuggestions();
    // Trigger full search
    this.hasSearched = true;
    this.performSearch();
  }

  performSearch() {
    const query = this.input.value.toLowerCase().trim();
    const translations = window.filesSearchTranslations || {};

    if (!query) {
      return;
    }

    if (!this.dataReady) {
      this.showError(translations.not_ready || 'Product data is not ready. Please try again.');
      return;
    }

    this.hasSearched = true;
    this.activity = 'searching';
    this.placeholderDismissed = true;
    this.clearPlaceholder();
    this.filesContainer.dataset.mode = 'results';
    this.hideSuggestions();
    this.showLoading('searching');

    // Simulate search delay for better UX
    setTimeout(() => {
      const filtered = this.searchProducts(query);

      this.hideLoading();
      this.toggleClearButton();

      if (filtered.length > 0) {
        this.filesContainer.data = filtered;
      } else {
        // Show no results message
        this.showNoResults(this.input.value);
      }
    }, 300);
  }

  showNoResults(searchTerm) {
    const translations = window.filesSearchTranslations || {};
    const noResultsLabel = translations.no_results_for || 'No results found for';
    const trySearchingLabel =
      translations.try_searching || 'Try searching by SKU or product title.';

    // Clear the data first
    this.filesContainer._data = [];
    this.filesContainer.dataset.mode = 'results';

    // Then set custom HTML
    this.filesContainer.innerHTML = `
      <div class="files-no-results" style="padding: 2rem 1rem; text-align: center; color: #666; background: #f9f9f9; border-radius: 8px; margin-top: 1rem;">
        <p style="font-size: 2rem; margin-bottom: 0.5rem;">🔍</p>
        <p style="font-size: 1.1rem; margin-bottom: 0.5rem;"><strong>${noResultsLabel} "${searchTerm}"</strong></p>
        <p style="font-size: 0.9rem; color: #999;">${trySearchingLabel}</p>
      </div>
    `;
    this.filesContainer.style.display = 'block';
  }

  // Tokenization + scoring helpers
  normalizeString(str) {
    if (str == null) return '';
    let s = String(str).toLowerCase();
    // Remove accents/diacritics
    s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Keep letters, numbers, spaces, hyphen, dot
    s = s.replace(/[^\w\s\-._/]/g, ' ');
    // Collapse whitespace
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  }

  tokenize(query) {
    const norm = this.normalizeString(query);
    // Split on spaces and common separators (space, hyphen, underscore, dot, slash)
    const tokens = norm.split(/[\s\-._\/]+/).filter(Boolean);
    const uniq = [];
    const seen = new Set();
    for (const t of tokens) {
      if (!seen.has(t)) {
        seen.add(t);
        uniq.push(t);
      }
    }
    return uniq;
  }

  // Approximate (fuzzy) word match against a set of words using Levenshtein distance
  // Only applies when the token has length >= 3 to avoid noisy matches
  approxTokenMatchInSet(token, wordSet) {
    const t = this.normalizeString(token);
    if (!t || t.length < 3) return false;

    for (const w of wordSet) {
      if (!w) continue;
      const d = this.calculateLevenshteinDistance(t, w);
      const ratio = 1 - d / Math.max(t.length, w.length);
      if (ratio >= 0.8 || (Math.max(t.length, w.length) <= 5 && d <= 1)) {
        return true;
      }
    }
    return false;
  }

  searchProducts(query) {
    // Word-by-word search on title, SKU, and handle only, with typo tolerance per word
    const tokens = this.tokenize(query);
    if (tokens.length === 0) return [];

    const results = [];
    const products = this.allProducts || [];

    for (const product of products) {
      const titleTokens = new Set(this.tokenize(product.title || ''));
      const skuTokens = new Set(this.tokenize(product.sku || ''));
      const handleTokens = new Set(this.tokenize(product.handle || ''));

      // Require EVERY query token to appear as a full word (exact or fuzzy) in any of the three fields
      let allPresent = true;
      // Track exact vs fuzzy matches per field so we can rank exact ahead of fuzzy
      let exactSkuMatches = 0,
        exactTitleMatches = 0,
        exactHandleMatches = 0;
      let fuzzySkuMatches = 0,
        fuzzyTitleMatches = 0,
        fuzzyHandleMatches = 0;

      for (const t of tokens) {
        const exactSku = skuTokens.has(t);
        const exactTitle = titleTokens.has(t);
        const exactHandle = handleTokens.has(t);
        const exact = exactSku || exactTitle || exactHandle;

        // Only consider fuzzy when exact wasn't found for that token in any field
        const fuzzySku = !exactSku && this.approxTokenMatchInSet(t, skuTokens);
        const fuzzyTitle = !exactTitle && this.approxTokenMatchInSet(t, titleTokens);
        const fuzzyHandle = !exactHandle && this.approxTokenMatchInSet(t, handleTokens);
        const fuzzy = !exact && (fuzzySku || fuzzyTitle || fuzzyHandle);

        if (!exact && !fuzzy) {
          allPresent = false;
          break;
        }

        // Accumulate counts
        if (exactSku) exactSkuMatches++;
        if (exactTitle) exactTitleMatches++;
        if (exactHandle) exactHandleMatches++;
        if (fuzzySku) fuzzySkuMatches++;
        if (fuzzyTitle) fuzzyTitleMatches++;
        if (fuzzyHandle) fuzzyHandleMatches++;
      }

      if (allPresent) {
        const totalExact = exactSkuMatches + exactTitleMatches + exactHandleMatches;
        const totalFuzzy = fuzzySkuMatches + fuzzyTitleMatches + fuzzyHandleMatches;
        results.push({
          product,
          // exact counts
          totalExact,
          exactSkuMatches,
          exactTitleMatches,
          exactHandleMatches,
          // fuzzy counts
          totalFuzzy,
          fuzzySkuMatches,
          fuzzyTitleMatches,
          fuzzyHandleMatches,
        });
      }
    }

    // Prioritization: ALL exact matches first, then fuzzy
    // Within exact: prefer more exact tokens, SKU > Title > Handle
    // Then use fuzzy counts similarly; finally alphabetical by title
    results.sort((a, b) => {
      if (b.totalExact !== a.totalExact) return b.totalExact - a.totalExact;
      if (b.exactSkuMatches !== a.exactSkuMatches) return b.exactSkuMatches - a.exactSkuMatches;
      if (b.exactTitleMatches !== a.exactTitleMatches)
        return b.exactTitleMatches - a.exactTitleMatches;
      if (b.exactHandleMatches !== a.exactHandleMatches)
        return b.exactHandleMatches - a.exactHandleMatches;
      if (b.totalFuzzy !== a.totalFuzzy) return b.totalFuzzy - a.totalFuzzy;
      if (b.fuzzySkuMatches !== a.fuzzySkuMatches) return b.fuzzySkuMatches - a.fuzzySkuMatches;
      if (b.fuzzyTitleMatches !== a.fuzzyTitleMatches)
        return b.fuzzyTitleMatches - a.fuzzyTitleMatches;
      if (b.fuzzyHandleMatches !== a.fuzzyHandleMatches)
        return b.fuzzyHandleMatches - a.fuzzyHandleMatches;
      return (a.product.title || '').localeCompare(b.product.title || '');
    });

    return results.map(r => r.product);
  }

  showLoading(activity = 'busy') {
    this.activity = activity;
    if (this.loadingSpinner) {
      this.loadingSpinner.style.display = 'flex';
    }
  }

  hideLoading() {
    this.activity = 'idle';
    if (this.loadingSpinner) {
      this.loadingSpinner.style.display = 'none';
    }
  }

  /**
   * Fuzzy matching with partial substring support
   * Supports partial matches (e.g., "SC" matches "SC-PY01", "SC-PY02", etc.)
   */
  fuzzyMatch(text, query) {
    if (!query) return true;
    if (!text) return false;

    // Direct substring check first (case-insensitive)
    if (text.includes(query)) return true;

    // Check if query matches the start of the text
    if (text.startsWith(query)) return true;

    // Check if query matches word boundaries (e.g., "SC" matches "SC-PY01")
    const words = text.split(/[-_\s]/);
    if (words.some(word => word.startsWith(query))) return true;

    // Allow for small typos using Levenshtein distance
    // Only apply for queries longer than 3 characters to avoid too many false positives
    if (query.length > 3) {
      return this.calculateLevenshteinDistance(text, query) <= 2;
    }

    return false;
  }

  /**
   * Calculate Levenshtein distance for fuzzy matching
   * Allows for typos and small variations
   */
  calculateLevenshteinDistance(a, b) {
    const tmp = [];
    for (let i = 0; i <= a.length; i++) {
      tmp[i] = [i];
    }

    for (let j = 0; j <= b.length; j++) {
      tmp[0][j] = j;
    }

    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        tmp[i][j] = Math.min(
          tmp[i - 1][j] + 1, // Deletion
          tmp[i][j - 1] + 1, // Insertion
          tmp[i - 1][j - 1] + cost // Substitution
        );
      }
    }

    return tmp[a.length][b.length];
  }

  /**
   * Debounce function to limit search frequency
   */
  debounce(func, wait = 300) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Define the custom element
customElements.define('files-search', FilesSearch);

// Expose shared APIs so other parts of the theme (e.g., predictive-search)
// can reuse the same logic without duplicating code.
// - window.FilesSearchAPI.searchProducts(query): product-level matching (exact-first + fuzzy)
// - window.FileUtils: lightweight helpers reused by predictive rendering
(function () {
  try {
    // Initialize namespaces if not present
    window.FilesSearchAPI = window.FilesSearchAPI || {};

    // Lazily create a FilesSearch instance solely for search logic reuse.
    // We avoid DOM attachment; we only need its methods and access to AkeneoFilesIndex.
    let _searchInstance = null;
    function getSearchInstance() {
      if (_searchInstance) {
        // Keep products fresh on each call
        _searchInstance.allProducts = window.AkeneoFilesIndex || [];
        return _searchInstance;
      }
      try {
        // Prefer real custom element instance if available
        _searchInstance = document.createElement('files-search');
      } catch (e) {
        // Fallback: direct class instance
        _searchInstance = new FilesSearch();
      }
      _searchInstance.allProducts = window.AkeneoFilesIndex || [];
      return _searchInstance;
    }

    // Only define searchProducts if not already provided
    if (typeof window.FilesSearchAPI.searchProducts !== 'function') {
      window.FilesSearchAPI.searchProducts = function (query) {
        const inst = getSearchInstance();
        return inst.searchProducts(query);
      };
    }

    // (Removed redundant FileUtils and secureDownload exports — provided by files-card-helpers.js)
  } catch (e) {
    // Fail-safe: do not break theme if exposing globals fails
    console.warn('FilesSearchAPI/FileUtils export failed:', e);
  }
})();
